import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import DocumentSelector from "./DocumentSelector";
import ReferenceAgentSelector from "./ReferenceAgentSelector";

const AgentConfigure = ({
    isOpen,
    onClose,
    sectionKey,
    agentKey,
    formData,
    updateAgentField,
    templateId,
    fabricTreeData,
    refAgentExpanded,
    onRefAgentExpandedChange,
    hideReferAgents = false,
    forceSelectData = false,
    groundingEnabled = false,
    onGroundingChange
}) => {
    const [activeTab, setActiveTab] = useState("selectData");
    const [selectedRefs, setSelectedRefs] = useState([]);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    // View mode for document grouping: 'collections' | 'meta_tags' | 'both'
    const [viewMode, setViewMode] = useState("collections");

    useEffect(() => {
        if (forceSelectData) {
            setActiveTab("selectData");
        }
    }, [forceSelectData]);

    // Load existing references when modal opens
    useEffect(() => {
        if (!isOpen) return;

        const existingRefs =
            formData.sectionsDict?.[sectionKey]?.agentsDict?.[agentKey]?.reference_agent_ids || [];
        setSelectedRefs(existingRefs);
    }, [isOpen, formData, sectionKey, agentKey]);

    useEffect(() => {
        if (!isOpen) return;
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    useEffect(() => {
        if (hideReferAgents && activeTab === "referAgent") {
            setActiveTab("selectData");
        }
    }, [hideReferAgents, activeTab]);

    useEffect(() => {
        if (isOpen) {
            setSearchInput("");
            setSearch("");
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const currentAgent = sectionKey
        ? formData.sectionsDict?.[sectionKey]?.agentsDict?.[agentKey]
        : formData.agentsDict?.[agentKey] || {};
    const agentName = currentAgent?.agent_name ?? currentAgent?.name ?? null;

    const agentNum =
        currentAgent?.agent_ordering_id ??
        currentAgent?.agent_number ??
        currentAgent?.ordering_id ??
        null;

    const agentDisplayName = agentName
        ? agentName
        : (agentNum ? `Agent ${agentNum}` : "Agent");

    const currentAgentId = currentAgent.id || currentAgent.temp_id || null;

    const handleReferenceChange = (ids) => {
        setSelectedRefs(ids);
        // live-update formData; template is persisted only via "Save Preset"
        updateAgentField(sectionKey, agentKey, "reference_agent_ids", ids);
    };

    const handleSearchInputChange = (e) => {
        setSearchInput(e.target.value);
    };

    const handleSearch = () => {
        setSearch(searchInput);
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    const handleClearSearch = () => {
        setSearchInput("");
        setSearch("");
    };

    const filterTreeData = (data, searchQuery) => {
        if (!searchQuery || !searchQuery.trim()) {
            return data;
        }

        const lowerSearch = searchQuery.toLowerCase().trim();

        return data
            .map((fabric) => {
                if (fabric.threads && Array.isArray(fabric.threads)) {
                    const filteredThreads = fabric.threads
                        .map((thread) => {
                            if (thread.documents && Array.isArray(thread.documents)) {
                                const filteredDocuments = thread.documents.filter((doc) => {
                                    return doc.document_name?.toLowerCase().includes(lowerSearch);
                                });

                                if (filteredDocuments.length > 0) {
                                    return {
                                        ...thread,
                                        documents: filteredDocuments,
                                        document_count: filteredDocuments.length,
                                    };
                                }
                            }
                            return null;
                        })
                        .filter((thread) => thread !== null);

                    if (filteredThreads.length > 0) {
                        return {
                            ...fabric,
                            threads: filteredThreads,
                            thread_count: filteredThreads.length,
                        };
                    }
                }
                return null;
            })
            .filter((fabric) => fabric !== null);
    };

    const filteredFabricTreeData = filterTreeData(fabricTreeData || [], search);

    const selectedDocCount = (currentAgent?.document_mappings || [])
        .filter(doc => doc.selected).length || 0;

    const currentDocIds = (currentAgent?.document_mappings || [])
        .filter(d => d.selected)
        .map(d => d.doc_id);
    const lastRunDocIds = Array.isArray(currentAgent?.last_run_doc_ids)
        ? currentAgent.last_run_doc_ids
        : [];
    const lastRunSet = new Set(lastRunDocIds);
    const currentSet = new Set(currentDocIds);
    const docsChanged =
        currentDocIds.length !== lastRunDocIds.length ||
        currentDocIds.some(id => !lastRunSet.has(id)) ||
        lastRunDocIds.some(id => !currentSet.has(id));
    const agentStatus = (currentAgent?.status || "").toUpperCase();
    const hasCompletedRun = agentStatus === "COMPLETED";
    // NULL = no snapshot taken yet (legacy/never-run agents) — suppress warning.
    const hasSnapshot = currentAgent?.last_run_doc_ids != null;
    const showStaleWarning = hasCompletedRun && hasSnapshot && docsChanged;

    return createPortal(
        <div className="modal fade modal-high-priority show d-block bg-dark bg-opacity-50" style={{ zIndex: "var(--num-1000000000 )" }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content ev-message-card2 d-block ev-card-2">

                    {/* HEADER */}
                    <div className="modal-header border-0 d-flex align-items-center justify-content-between">
                        {/* minWidth 0 lets the name pill shrink and truncate instead of pushing Validate/close out */}
                        <div className="d-flex align-items-center gap-2" style={{ flex: 1, minWidth: 0 }}>
                            <h5 className="ev-med-p-blue mb-0 text-nowrap" style={{ flexShrink: 0 }}>
                                Configure Agent
                            </h5>

                            <span className="ev-message-card3 ev-fs-12 px-2 py-1 d-flex align-items-center avatar-tooltip-bottom" data-tooltip={agentDisplayName} style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                                <i className="bi bi-robot me-1 ev-fs-14" style={{ flexShrink: 0 }}></i>
                                <span className="text-truncate">{agentDisplayName}</span>
                            </span>

                            {/* Temporarily hide the Validate feature from the frontend.
                            <label
                                className={`ev-fs-13 m-0 text-nowrap d-flex align-items-center avatar-tooltip px-2 py-0 ev-message-card3  ${currentAgent?.id ? '' : 'opacity-50'}`}
                                style={{ flexShrink: 0 }}
                                data-tooltip={"Check this agent's output against its selected sources."
                                        }
                            >

                                <span className={groundingEnabled ? 'ev-bold-reg px-1' : 'px-1'}>
                                    Validate
                                </span>
                                <span
                                    className="form-check form-switch d-flex align-items-center px-1"
                                >
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="agent-grounding-check"
                                        checked={groundingEnabled}
                                        disabled={!currentAgent?.id}
                                        onChange={(e) => onGroundingChange && onGroundingChange(e.target.checked)}
                                        style={{ margin: 0, float: 'none', cursor: 'inherit' }}
                                    />
                                </span>
                            </label>
                            */}
                        </div>

                        <button
                            type="button"
                            className="ev-btn-text-only-fill ev-fs-20 ms-2"
                            style={{ flexShrink: 0 }}
                            onClick={onClose}
                        >
                            <i className="bi bi-x"></i>
                        </button>
                    </div>

                    {/* TABS */}
                    {!hideReferAgents && (
                        <div className="ev-card-3 margin-left-2 m-0 ev-padding-xsmall ps-0 w-95 d-flex align-items-center">
                            <div className="btn-group custom-btn-group-outer my-1 mx-2">
                                <button
                                    className={`btn custom-btn-group-inner ${activeTab === "selectData"
                                        ? "ev-btn-blue-fill-round"
                                        : "ev-btn-lightblue-fill-round"
                                        }`}
                                    onClick={() => setActiveTab("selectData")}
                                >
                                    Select Data
                                    {selectedDocCount > 0 && (
                                        <span className="ms-1 ev-fs-10">({selectedDocCount})</span>
                                    )}
                                </button>

                                <button
                                    className={`btn custom-btn-group-inner ${activeTab === "referAgent"
                                        ? "ev-btn-blue-fill-round"
                                        : "ev-btn-lightblue-fill-round"
                                        }`}
                                    onClick={() => setActiveTab("referAgent")}
                                >
                                    Refer Agents
                                    {selectedRefs.length > 0 && (
                                        <span className="ms-1 ev-fs-10">({selectedRefs.length})</span>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* BODY */}
                    <div className="modal-body scrollable-element vh-60">

                        {/* TAB: SELECT DATA */}
                        {activeTab === "selectData" && (
                            <div>
                                {showStaleWarning && (
                                    <div className="px-1 mb-2">
                                        <div className="alert alert-warning d-flex align-items-center py-2 mb-0" role="alert">
                                            <i className="bi bi-exclamation-triangle me-2"></i>
                                            <span className="ev-fs-13">
                                                Documents changed since the last run. A fresh run is recommended — the previous response's references may be stale.
                                            </span>
                                        </div>
                                    </div>
                                )}
                                <div className="position-relative mb-3 px-1 w-40">
                                    <input
                                        type="text"
                                        className="form-control search-input ms-0"
                                        placeholder="Search documents by name"
                                        value={searchInput}
                                        onChange={handleSearchInputChange}
                                        onKeyPress={handleSearchKeyPress}
                                    />
                                    <button
                                        type="button"
                                        className="ev-btn-lightblue-fill-round search-bar-button"
                                        onClick={handleSearch}
                                    >
                                        <i className="bi bi-search"></i>
                                    </button>

                                    </div>

                                {search && filteredFabricTreeData.length === 0 ? (
                                    <div className="px-3 mb-2">
                                        <div className="alert alert-warning d-flex align-items-center" role="alert">
                                            <i className="bi bi-exclamation-triangle me-2"></i>
                                            <span>No documents found for "{search}". Try a different search term.</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="embedded-document-selector">
                                        <DocumentSelector
                                            isEmbedded={true}
                                            isOpen={true}
                                            fabricTreeData={filteredFabricTreeData}
                                            viewMode={viewMode}
                                            onViewModeChange={setViewMode}
                                            selectedDocIds={
                                                (sectionKey
                                                    ? formData.sectionsDict?.[sectionKey]?.agentsDict?.[agentKey]
                                                    : formData.agentsDict?.[agentKey]
                                                )?.document_mappings
                                                    ?.filter(doc => doc.selected)
                                                    ?.map(doc => doc.doc_id) || []
                                            }
                                            onDocumentSelect={(ids) => {
                                                const mappings = ids.map(id => ({
                                                    doc_id: id,
                                                    selected: true
                                                }));

                                                if (sectionKey) {
                                                    updateAgentField(sectionKey, agentKey, "document_mappings", mappings);
                                                } else {
                                                    updateAgentField(agentKey, "document_mappings", mappings);
                                                }
                                            }}
                                            onClose={() => { }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* TAB: REFER AGENTS */}
                        {!hideReferAgents && activeTab === "referAgent" && (
                            <div className="ps-3 pe-3">
                                <ReferenceAgentSelector
                                    formData={formData}
                                    sectionKey={sectionKey}
                                    agentKey={agentKey}
                                    currentAgentId={currentAgentId}
                                    selectedRefs={selectedRefs}
                                    onChange={handleReferenceChange}
                                    persistedExpanded={refAgentExpanded}
                                    onExpandedChange={onRefAgentExpandedChange}
                                />
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>,
    document.body
    );
};

export default AgentConfigure;